// CSV exports (collections sprint, Instance D). One download path for every export button.
//
// The server does all the work that matters — the page's own query, the row cap, the
// export log, the PHI banner, formula neutralising — so this only fetches the file and
// hands it to the browser. A fetch rather than an <a href>, because Atlas authenticates
// /api/* with the Clerk session token authFetch.js attaches, which a plain link cannot carry.

const API_URL = process.env.REACT_APP_API_URL || "";

// The filename comes from the server (kind, practice id and time — never a patient name).
// The fallback is used only if the header is missing.
export function filenameFrom(disposition, fallback) {
  const match = (disposition || "").match(
    /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i,
  );
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch (_) {
    return match[1];
  }
}

// `params` is a URLSearchParams (or anything that stringifies to a query string) built by
// the SAME function the page uses for its list request, so the file matches the screen.
// Resolves with the filename; rejects with an Error whose message is safe to show.
//
// `path` (optional) is the CSV's route under the practice, without ".csv", for an export
// that is not a list page's — a Reports page CSV is `reports/<name>`. Lists use
// `export/<kind>`. A `path` starting with "/" is an absolute API path instead, for the one
// export that has no practice in its URL (the Mission Control grid). `ext` is the file
// type the route serves, "csv" unless it says otherwise (the weekly report's "pdf").
export async function downloadExport(practiceId, kind, params, path = null, ext = "csv") {
  const query = params ? String(params) : "";
  const route = path && path.startsWith("/")
    ? path
    : `/api/practices/${practiceId}/${path || `export/${kind}`}`;
  const url = `${API_URL}${route}.${ext}${query ? `?${query}` : ""}`;
  const response = await fetch(url, { credentials: "include" });
  // A 202 is "computing" (the weekly team report, 2026-10-23): `ok` but no file, so
  // saving it would hand the user a JSON body named like a CSV.
  if (response.status === 202) {
    throw new Error(
      "Still computing on the server; try the export again in a few seconds.",
    );
  }
  if (!response.ok) {
    let message = "The export could not be downloaded.";
    try {
      const body = await response.json();
      if (body && body.error) message = body.error;
    } catch (_) {
      /* not JSON */
    }
    throw new Error(message);
  }
  const filename = filenameFrom(
    response.headers.get("Content-Disposition"),
    `atlas-${kind}.${ext}`,
  );
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
  return filename;
}
